import React from "react";

type InlineEditorProps = {
    value: string;
    isEdit: boolean;
    onClick?: React.MouseEventHandler<HTMLSpanElement>;
    onChangeValue?: (value: string) => void;
    onCancel?: () => void;   // Escape
    onDecide?: () => void;   // Enter
    onMovePrev?: () => void; // Shift + Tab
    onMoveNext?: () => void; // Tab
    onLeave?: () => void;
}

export const InlineEditor = (props: InlineEditorProps) => {

    const [value, setValue] = React.useState(props.value);
    const [inComposition, setInComposition] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (props.value !== value) {
            setValue(props.value);
        }
        if (props.isEdit) {
            inputRef.current?.focus();
        }
    }, [props.isEdit]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        e.stopPropagation();

        if (inComposition) {
            return;
        }

        if (e.code === 'Escape') {
            props.onCancel?.();
            props.onLeave?.();
        } else if (e.code === 'Enter' || e.code === 'NumpadEnter') {
            props.value !== value && props.onChangeValue?.(value);
            props.onDecide?.();
            props.onLeave?.();
        } else if (e.code === 'Tab') {
            props.value !== value && props.onChangeValue?.(value);
            if (e.shiftKey) {
                props.onMovePrev?.();
                props.onLeave?.();
            } else {
                props.value !== value && props.onChangeValue?.(value);
                props.onMoveNext?.();
                props.onLeave?.();
            }
        }

    }

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
        props.value !== value && props.onChangeValue?.(value);
        props.onLeave?.();
    }

    if (props.isEdit) {
        return <input type="text"
                      ref={inputRef}
                      value={value}
                      onKeyDown={handleKeyDown}
                      onCompositionStart={() => setInComposition(true)}
                      onCompositionEnd={() => setInComposition(false)}
                      onChange={(e) => setValue(e.target.value)}
                      onBlur={handleBlur}
        />;
    } else {
        return <span onClick={props.onClick}>{props.value}</span>;
    }
}

type ClickableInlineEditorProps = {
    value: string;
    readOnly: boolean;
    onChange?: (value: string) => void;
}

export const ClickableInlineEditor = (props: ClickableInlineEditorProps) => {

    const [isEdit, setIsEdit] = React.useState(false);

    function startEdit() {
        !props.readOnly && setIsEdit(true);
    }

    function endEdit() {
        setIsEdit(false);
    }

    function onChangeValue(value: string) {

        if (props.value !== value) {
            props.onChange?.(value);
        }
    }

    return <InlineEditor value={props.value} isEdit={isEdit}
                         onClick={startEdit} onChangeValue={onChangeValue}
                         onLeave={endEdit}
    />;
}
